import React, { useState, useEffect } from "react";
import { motion, AnimatePresence, useScroll, useMotionValueEvent } from "framer-motion";
import { useTranslation } from "react-i18next";

function LanguageSwitcher({ currentLang, onSelectLang }) {
  const languages = [
    { code: "en", label: "EN" },
    { code: "ru", label: "RU" },
    { code: "uz", label: "UZ" },
  ];

  return (
    <div className="doni-lang-switcher">
      {languages.map((n) => {
        const isCurrent = currentLang === n.code;
        return (
          <button
            key={n.code}
            type="button"
            onClick={() => onSelectLang(n.code)}
            aria-label={`Switch to ${n.label}`}
            aria-pressed={isCurrent}
            className="doni-lang-btn clickable"
          >
            {isCurrent && (
              <motion.span
                layoutId="language-pill"
                className="doni-lang-pill"
                transition={{ type: "spring", damping: 25, stiffness: 300 }}
              />
            )}
            <span className={`doni-lang-label ${isCurrent ? "active" : ""}`}>
              {n.label}
            </span>
          </button>
        );
      })}
    </div>
  );
}

function ThemeToggle({ theme, toggleTheme }) {
  const isDark = theme === "dark";

  return (
    <button
      type="button"
      onClick={toggleTheme}
      aria-label={`Switch to ${isDark ? "light" : "dark"} mode`}
      className="doni-theme-toggle clickable"
    >
      <motion.span
        className="doni-theme-knob"
        animate={{ 
          x: isDark ? 22 : 0,
          rotate: isDark ? 360 : 0
        }}
        transition={{ type: "spring", damping: 22, stiffness: 280 }}
      >
        <span style={{ display: "inline-block", fontSize: "11px", lineHeight: 1 }}>
          {isDark ? "☾" : "☀"}
        </span>
      </motion.span>
    </button>
  );
}

const Navbar = () => {
  const { t, i18n } = useTranslation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState("");
  const [theme, setTheme] = useState(localStorage.getItem("theme") || "light");
  const { scrollY } = useScroll();

  // Keep theme synced with document
  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
    document.documentElement.classList.toggle("dark", theme === "dark");
    localStorage.setItem("theme", theme);
  }, [theme]);

  const toggleTheme = () => {
    setTheme((prev) => (prev === "dark" ? "light" : "dark"));
  };

  // Ultra-smooth scroll detection with hysteresis to eliminate flicker
  useMotionValueEvent(scrollY, "change", (latest) => {
    if (latest > 45 && !isScrolled) {
      setIsScrolled(true);
    } else if (latest < 20 && isScrolled) {
      setIsScrolled(false);
    }
  });

  // Also sync with Lenis scroll if available for 0ms latency
  useEffect(() => {
    if (!window.lenis) return;
    const onLenisScroll = ({ scroll }) => {
      if (scroll > 45 && !isScrolled) {
        setIsScrolled(true);
      } else if (scroll < 20 && isScrolled) {
        setIsScrolled(false);
      }
    };
    window.lenis.on("scroll", onLenisScroll);
    return () => {
      window.lenis?.off("scroll", onLenisScroll);
    };
  }, [isScrolled]);

  // Section spy to softly highlight active link
  useEffect(() => {
    const sections = ["about", "experience", "projects", "achievements", "contact"];
    const handleScrollSpy = () => {
      const scrollPos = (window.lenis ? window.lenis.scroll : window.scrollY) + 160;
      for (let i = sections.length - 1; i >= 0; i--) {
        const el = document.getElementById(sections[i]);
        if (el && el.offsetTop <= scrollPos) {
          setActiveSection(sections[i]);
          return;
        }
      }
      if ((window.lenis ? window.lenis.scroll : window.scrollY) < 180) {
        setActiveSection("");
      }
    };

    window.addEventListener("scroll", handleScrollSpy, { passive: true });
    return () => window.removeEventListener("scroll", handleScrollSpy);
  }, []);

  const currentLang =
    ["en", "ru", "uz"].find((code) => i18n.language?.startsWith(code)) || "en";

  const changeLanguage = (code) => {
    i18n.changeLanguage(code);
  };

  const navLinks = [
    { href: "#about", id: "about", label: t("nav_about") },
    { href: "#experience", id: "experience", label: t("nav_experience") },
    { href: "#projects", id: "projects", label: t("nav_projects") },
    { href: "#achievements", id: "achievements", label: t("nav_achievements") },
    { href: "#contact", id: "contact", label: t("nav_contact") },
  ];

  // Silky smooth scroll navigation via Lenis
  const handleScrollTo = (href) => (e) => {
    e.preventDefault();
    setIsMenuOpen(false);
    if (href === "#top" || href === "#profile") {
      if (window.lenis) {
        window.lenis.scrollTo(0, { duration: 1.1 });
      } else {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
    } else {
      const el = document.querySelector(href);
      if (el) {
        if (window.lenis) {
          window.lenis.scrollTo(el, { offset: -70, duration: 1.1 });
        } else {
          el.scrollIntoView({ behavior: "smooth" });
        }
      }
    }
  };

  const isDark = theme === "dark";

  return (
    <header className="doni-header">
      <motion.nav
        className="doni-nav"
        initial={false}
        animate={{
          maxWidth: isScrolled ? "820px" : "1024px",
          borderRadius: isScrolled ? "9999px" : "0px",
          backgroundColor: isScrolled
            ? isDark
              ? "rgba(18, 18, 16, 0.78)"
              : "rgba(255, 255, 255, 0.78)"
            : "rgba(0, 0, 0, 0)",
          borderColor: isScrolled
            ? isDark
              ? "rgba(255, 255, 255, 0.12)"
              : "rgba(0, 0, 0, 0.08)"
            : "rgba(0, 0, 0, 0)",
          boxShadow: isScrolled
            ? isDark
              ? "0 12px 35px -10px rgba(0, 0, 0, 0.6)"
              : "0 8px 30px -12px rgba(0, 0, 0, 0.14)"
            : "0 0 0 0 rgba(0, 0, 0, 0)",
          paddingTop: isScrolled ? "0.55rem" : "0.75rem",
          paddingBottom: isScrolled ? "0.55rem" : "0.75rem",
        }}
        transition={{
          duration: 0.42,
          ease: [0.16, 1, 0.3, 1],
        }}
        style={{
          willChange: "max-width, border-radius, background-color, border-color, box-shadow, padding",
        }}
      >
        {/* Left: Clean Minimalist Text Logo */}
        <a
          href="#profile"
          onClick={handleScrollTo("#profile")}
          className="doni-logo-text-link clickable"
          aria-label="Akbarshoh Ismatov — back to top"
        >
          Akbarshoh<span className="doni-logo-dot">.dev</span>
        </a>

        {/* Center: Desktop navigation links */}
        <div className="doni-links-desktop">
          {navLinks.map((link) => {
            const isActive = activeSection === link.id;
            return (
              <a
                key={link.href}
                href={link.href}
                onClick={handleScrollTo(link.href)}
                className={`doni-link clickable ${isActive ? "active" : ""}`}
              >
                {link.label}
              </a>
            );
          })}
        </div>

        {/* Right: Controls */}
        <div className="doni-controls">
          <div className="doni-lang-desktop">
            <LanguageSwitcher
              currentLang={currentLang}
              onSelectLang={changeLanguage}
            />
          </div>
          <ThemeToggle theme={theme} toggleTheme={toggleTheme} />

          {/* Mobile hamburger toggle */}
          <button
            type="button"
            className="doni-burger-btn clickable"
            onClick={() => setIsMenuOpen((prev) => !prev)}
            aria-label="Toggle menu"
          >
            <motion.span
              className="doni-burger-bar"
              animate={{ rotate: isMenuOpen ? 45 : 0, y: isMenuOpen ? 3.5 : 0 }}
              transition={{ type: "spring", damping: 22, stiffness: 300 }}
            />
            <motion.span
              className="doni-burger-bar"
              animate={{ rotate: isMenuOpen ? -45 : 0, y: isMenuOpen ? -3.5 : 0 }}
              transition={{ type: "spring", damping: 22, stiffness: 300 }}
            />
          </button>
        </div>
      </motion.nav>

      {/* Mobile menu dropdown with spring animation */}
      <AnimatePresence>
        {isMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -12, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -12, scale: 0.98 }}
            transition={{ type: "spring", damping: 25, stiffness: 320 }}
            className="doni-mobile-dropdown"
          >
            {navLinks.map((link, idx) => {
              const isActive = activeSection === link.id;
              return (
                <motion.a
                  key={link.href}
                  href={link.href}
                  onClick={handleScrollTo(link.href)}
                  className={`doni-mobile-link clickable ${isActive ? "active" : ""}`}
                  initial={{ opacity: 0, x: -8 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: idx * 0.04 }}
                >
                  {link.label}
                </motion.a>
              );
            })}
            <div className="doni-mobile-lang-wrap">
              <LanguageSwitcher
                currentLang={currentLang}
                onSelectLang={changeLanguage}
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Navbar;
